import React, { useState } from 'react';
import { 
  Car, 
  Calendar, 
  DollarSign, 
  User, 
  Settings, 
  Bell, 
  Menu, 
  X,
  LogOut,
  MapPin,
  Clock
} from 'lucide-react';

interface DriverLayoutProps {
  children: React.ReactNode;
}

const DriverLayout: React.FC<DriverLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/driver/dashboard', icon: Car, current: true },
    { name: 'My Bookings', href: '/driver/bookings', icon: Calendar, current: false },
    { name: 'Earnings', href: '/driver/earnings', icon: DollarSign, current: false },
    { name: 'Vehicle Info', href: '/driver/vehicle', icon: Car, current: false },
    { name: 'Availability', href: '/driver/availability', icon: Clock, current: false },
    { name: 'Profile', href: '/driver/profile', icon: User, current: false },
    { name: 'Notifications', href: '/driver/notifications', icon: Bell, current: false },
    { name: 'Settings', href: '/driver/settings', icon: Settings, current: false },
  ];

  const driverInfo = {
    name: 'John Driver',
    email: 'john.driver@dachauffeur.com',
    status: 'available',
    vehicle: 'Mercedes S-Class',
    plateNumber: 'DRV-1234',
    rating: 4.8,
    totalTrips: 342
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center">
              <Car className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">DaChauffeur</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Driver Info Card */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">{driverInfo.name}</h3>
                <p className="text-xs text-gray-500">{driverInfo.email}</p>
                <div className="flex items-center mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {driverInfo.status}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">⭐ {driverInfo.rating}</span>
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-600">
              <div className="flex justify-between">
                <span>Vehicle:</span>
                <span className="font-medium">{driverInfo.vehicle}</span>
              </div>
              <div className="flex justify-between">
                <span>Plate:</span>
                <span className="font-medium">{driverInfo.plateNumber}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Trips:</span>
                <span className="font-medium">{driverInfo.totalTrips}</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`
                  group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                  ${item.current 
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </a>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <button className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors">
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-400 hover:text-gray-600"
            >
              <Menu size={24} />
            </button>

            <div className="flex items-center space-x-4">
              {/* Quick Status */}
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <MapPin size={16} />
                <span>Current Location: Kabul</span>
              </div>

              {/* Notifications */}
              <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <Bell size={20} />
                <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DriverLayout;
